import React, { useEffect, useRef, useState } from 'react';
import { useCompact } from './_layout';
import { playWord, playOops } from './_audio';

const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5', sky: '#BAE6FD',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';

const ASSETS = '/games/vocab_train_spell';

const ANIM_CSS = `
@keyframes tsRoll { 0%{transform:translateX(-50%)} 100%{transform:translateX(calc(-50% + 70vw))} }
@keyframes tsCarShake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-4px)} 75%{transform:translateX(4px)} }
@keyframes tsSmoke { 0%{transform:translate(0,0) scale(0.6); opacity:.85} 100%{transform:translate(-30px,-50px) scale(1.4); opacity:0} }
@keyframes tsRibbon { 0%{transform:translate(0,0) rotate(0); opacity:1} 100%{transform:translate(var(--dx),var(--dy)) rotate(var(--dr)); opacity:0} }
@keyframes tsBob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2px)} }
@keyframes tsFly {
  0%   { transform: translate(-50%, -50%) scale(1)    rotate(0); }
  55%  { transform: translate(calc(-50% + var(--toX) * 0.55), calc(-50% + var(--toY) * 0.55 - 28px)) scale(1.2) rotate(-220deg); }
  100% { transform: translate(calc(-50% + var(--toX)), calc(-50% + var(--toY))) scale(1) rotate(-360deg); }
}
.ts-tile-dragging { z-index: 1000; pointer-events: none; transform: scale(1.15); box-shadow: 0 8px 20px rgba(0,0,0,0.35) !important; }
`;

// Quick "pluck" SFX when a letter is clicked.
function playTilePluck() {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  let ctx;
  try { ctx = new AC(); } catch (e) { return; }
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(880, now);
  osc.frequency.exponentialRampToValueAtTime(440, now + 0.18);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.22, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.22);
  setTimeout(() => { try { ctx.close(); } catch (e) {} }, 350);
}

// "Tu tu" whistle — 2 sawtooth toots through lowpass. Total duration ~780ms.
function playWhistle() {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  let ctx;
  try { ctx = new AC(); } catch (e) { return; }
  const now = ctx.currentTime;

  const playToot = (startT, freq, dur, vol = 0.18) => {
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(freq * 0.92, startT);
    osc.frequency.linearRampToValueAtTime(freq, startT + 0.05);
    osc.frequency.linearRampToValueAtTime(freq * 0.95, startT + dur);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, startT);
    gain.gain.linearRampToValueAtTime(vol, startT + 0.04);
    gain.gain.linearRampToValueAtTime(vol * 0.85, startT + dur - 0.05);
    gain.gain.linearRampToValueAtTime(0, startT + dur);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1500;
    osc.connect(filter).connect(gain).connect(ctx.destination);
    osc.start(startT);
    osc.stop(startT + dur + 0.05);
  };

  playToot(now, 520, 0.28);
  playToot(now + 0.38, 520, 0.4);

  setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1200);
}

// Chuff-chuff puffs — 8 lowpass-filtered noise bursts. Plays while train rolls.
function playChuffs() {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  let ctx;
  try { ctx = new AC(); } catch (e) { return; }
  const now = ctx.currentTime;

  for (let i = 0; i < 8; i++) {
    const t = now + i * 0.22;
    const bufSize = Math.floor(ctx.sampleRate * 0.14);
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let j = 0; j < bufSize; j++) data[j] = (Math.random() * 2 - 1) * 0.5;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 380;
    filter.Q.value = 6;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.22, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    noise.connect(filter).connect(gain).connect(ctx.destination);
    noise.start(t);
    noise.stop(t + 0.14);
  }

  setTimeout(() => { try { ctx.close(); } catch (e) {} }, 2500);
}

function injectCss() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('train-anim-css')) return;
  const el = document.createElement('style');
  el.id = 'train-anim-css';
  el.textContent = ANIM_CSS;
  document.head.appendChild(el);
}

export default function TrainSpellGame({ payload, onAnswer, disabled }) {
  const compact = useCompact();
  const letters = payload.answer_letters || [];
  const tiles = payload.letter_tiles || [];
  const numCars = payload.num_cars || letters.length;

  const initialPool = () => tiles.map((l, i) => ({ id: `t${i}`, letter: l, placedAt: null, flying: false }));
  const [pool, setPool] = useState(initialPool);
  const [drag, setDrag] = useState(null);
  const [flying, setFlying] = useState([]); // [{ id, letter, startX, startY, toX, toY }]
  const [grading, setGrading] = useState(null);
  const [retries, setRetries] = useState(0);
  const [chugging, setChugging] = useState(false);
  const [wordZoom, setWordZoom] = useState(false);
  const stageRef = useRef(null);
  const carRefs = useRef([]);
  const tileRefs = useRef({});
  const startPosRef = useRef(null);
  const wasDraggedRef = useRef(false);
  // Synchronous reservation of car indices for in-flight tiles. Without this,
  // two rapid clicks both compute firstEmpty from the same stale `pool` and
  // collide on the same car → one letter ends up lost.
  const pendingPlacementsRef = useRef(new Map()); // tileId → carIdx

  useEffect(() => { injectCss(); }, []);

  // Auto-play audio once per word
  useEffect(() => {
    let cleanup = null;
    const t = setTimeout(() => {
      cleanup = playWord(payload.audio_url, payload.tts_text || payload.word_en);
    }, 350);
    return () => {
      clearTimeout(t);
      if (cleanup) cleanup();
    };
  }, [payload.audio_url, payload.tts_text, payload.word_en]);

  const replay = () => playWord(payload.audio_url, payload.tts_text || payload.word_en);

  const cars = Array.from({ length: numCars }, (_, i) => {
    const t = pool.find(p => p.placedAt === i);
    return t || null;
  });

  // ── Drag / click handlers (threshold 6px distinguishes click vs drag) ──
  const getPoint = (e) => ('touches' in e ? e.touches[0] : e);
  const CLICK_THRESHOLD_PX = 6;

  const placeInFirstEmptyCar = (tileId) => {
    // Already in flight — ignore duplicate click on the same tile.
    if (pendingPlacementsRef.current.has(tileId)) return;

    // Cars already occupied OR reserved by tiles currently flying.
    const placedCars = new Set(pool.filter(p => p.placedAt !== null).map(p => p.placedAt));
    const pendingCars = new Set(pendingPlacementsRef.current.values());

    let firstEmpty = -1;
    for (let i = 0; i < numCars; i++) {
      if (!placedCars.has(i) && !pendingCars.has(i)) { firstEmpty = i; break; }
    }
    if (firstEmpty < 0) return;

    const tile = pool.find(p => p.id === tileId);
    if (!tile) return;

    const tileEl = tileRefs.current[tileId];
    const carEl = carRefs.current[firstEmpty];
    if (!tileEl || !carEl) {
      // Fallback: instant place if refs unavailable
      setPool(prev => prev.map(p => p.id === tileId ? { ...p, placedAt: firstEmpty } : p));
      return;
    }

    // Reserve the car synchronously so subsequent rapid clicks skip past it.
    pendingPlacementsRef.current.set(tileId, firstEmpty);

    const tileRect = tileEl.getBoundingClientRect();
    const carRect = carEl.getBoundingClientRect();
    const startX = tileRect.left + tileRect.width / 2;
    const startY = tileRect.top + tileRect.height / 2;
    const endX = carRect.left + carRect.width / 2;
    const endY = carRect.top + carRect.height / 2;

    playTilePluck();
    setFlying(f => [...f, {
      id: tileId, letter: tile.letter,
      startX, startY,
      toX: endX - startX, toY: endY - startY,
    }]);
    // Hide source tile while it's mid-flight
    setPool(prev => prev.map(p => p.id === tileId ? { ...p, flying: true } : p));

    setTimeout(() => {
      setPool(prev => {
        // Defense: if our reserved car was somehow taken (e.g. by a drag drop),
        // find a new empty spot rather than colliding with the other tile.
        const occupant = prev.find(p => p.placedAt === firstEmpty);
        let target = firstEmpty;
        if (occupant && occupant.id !== tileId) {
          const used = new Set(prev.filter(p => p.placedAt !== null).map(p => p.placedAt));
          target = -1;
          for (let i = 0; i < numCars; i++) {
            if (!used.has(i)) { target = i; break; }
          }
        }
        if (target < 0) {
          // No empty car — release the tile back to the tray.
          return prev.map(p => p.id === tileId ? { ...p, flying: false } : p);
        }
        return prev.map(p => p.id === tileId
          ? { ...p, placedAt: target, flying: false }
          : p);
      });
      setFlying(f => f.filter(x => x.id !== tileId));
      pendingPlacementsRef.current.delete(tileId);
    }, 450);
  };

  const startDrag = (e, tileId) => {
    if (disabled || grading === 'correct' || grading === 'revealed') return;
    const point = getPoint(e);
    startPosRef.current = { x: point.clientX, y: point.clientY };
    wasDraggedRef.current = false;
    setDrag({ id: tileId, x: point.clientX, y: point.clientY });
    e.preventDefault();
  };

  const endDrag = (e) => {
    if (!drag) return;

    // CLICK (movement < threshold) → place into first empty car
    if (!wasDraggedRef.current) {
      placeInFirstEmptyCar(drag.id);
      setDrag(null);
      startPosRef.current = null;
      return;
    }

    // DRAG → drop on car under cursor (or first empty if dropped in tray area)
    const point = getPoint(e);
    const x = point.clientX, y = point.clientY;
    let dropCarIdx = -1;
    for (let i = 0; i < carRefs.current.length; i++) {
      const car = carRefs.current[i];
      if (!car) continue;
      const r = car.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        dropCarIdx = i;
        break;
      }
    }
    if (dropCarIdx >= 0) {
      setPool(prev => prev.map(p => {
        if (p.placedAt === dropCarIdx) return { ...p, placedAt: null };
        if (p.id === drag.id) return { ...p, placedAt: dropCarIdx };
        return p;
      }));
    } else {
      // Dropped outside any car — treat as a click so a tiny accidental drift
      // during rapid clicks doesn't swallow the letter.
      placeInFirstEmptyCar(drag.id);
    }
    setDrag(null);
    startPosRef.current = null;
  };

  useEffect(() => {
    if (!drag) return;
    const move = (e) => {
      const p = ('touches' in e ? e.touches[0] : e);
      if (startPosRef.current && !wasDraggedRef.current) {
        const dx = p.clientX - startPosRef.current.x;
        const dy = p.clientY - startPosRef.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > CLICK_THRESHOLD_PX) {
          wasDraggedRef.current = true;
        }
      }
      setDrag(d => d ? { ...d, x: p.clientX, y: p.clientY } : null);
      if (wasDraggedRef.current) e.preventDefault();
    };
    const up = (e) => endDrag(e);
    window.addEventListener('mousemove', move, { passive: false });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
    // eslint-disable-next-line
  }, [drag]);

  // Chuff-chuff SFX plays while train rolls
  useEffect(() => {
    if (chugging) playChuffs();
  }, [chugging]);

  const ejectFromCar = (carIdx) => {
    if (disabled || grading === 'correct' || grading === 'revealed') return;
    setPool(prev => prev.map(p => p.placedAt === carIdx ? { ...p, placedAt: null } : p));
  };

  // Auto-grade
  useEffect(() => {
    if (grading === 'correct' || grading === 'revealed') return;
    const allFilled = cars.every(c => c !== null);
    if (!allFilled) {
      if (grading === 'wrong') setGrading(null);
      return;
    }
    const guess = cars.map(c => c.letter).join('');
    const answer = letters.join('');
    if (guess === answer) {
      setGrading('correct');
      // Sequence: whistle "tu tu" → zoom word card (word + meaning + definition)
      // + replay EN audio → chug off. Extra second so the child can read.
      playWhistle();
      setTimeout(() => {
        setWordZoom(true);
        playWord(payload.audio_url, payload.tts_text || payload.word_en);
      }, 900);
      setTimeout(() => {
        setChugging(true);
      }, 2400);
      setTimeout(() => {
        if (onAnswer) onAnswer({
          is_correct: true,
          user_answer: guess.toLowerCase(),
        });
      }, 6000);
    } else {
      setGrading('wrong');
      playOops();
    }
    // eslint-disable-next-line
  }, [pool]);

  const ejectAll = () => {
    setPool(prev => prev.map(p => ({ ...p, placedAt: null })));
    setRetries(r => r + 1);
    setGrading(null);
  };

  // Child chose to see the answer: fill the train correctly, show the full
  // word card, but the question does NOT count as passed (is_correct: false).
  const revealAnswer = () => {
    if (grading === 'correct' || grading === 'revealed') return;
    setGrading('revealed');
    setPool(prev => {
      const used = new Set();
      const next = prev.map(p => ({ ...p, placedAt: null, flying: false }));
      letters.forEach((ch, carIdx) => {
        const t = next.find(p => !used.has(p.id) && p.letter === ch);
        if (t) { used.add(t.id); t.placedAt = carIdx; }
      });
      return next;
    });
    setWordZoom(true);
    playWord(payload.audio_url, payload.tts_text || payload.word_en);
    setTimeout(() => {
      if (onAnswer) onAnswer({ is_correct: false, user_answer: 'revealed' });
    }, 5000);
  };

  const allPlaced = pool.every(p => p.placedAt !== null);

  return (
    <div style={{
      width: 'calc(100vw - 32px)',
      maxWidth: 1600,
      marginLeft: 'calc(-50vw + 50% + 16px)',
      marginRight: 'calc(-50vw + 50% + 16px)',
      fontFamily: FONT_BODY,
      padding: 0,
      height: 'calc(100vh - 120px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div
        ref={stageRef}
        style={{
          position: 'relative',
          width: compact ? '100%' : 'min(100%, calc((100vh - 120px) * 1672 / 941))',
          aspectRatio: compact ? '3 / 4' : '1672 / 941',
          borderRadius: 24,
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(139,111,71,0.18)',
          userSelect: 'none',
        }}
      >
        {/* Background */}
        <img
          src={`${ASSETS}/background.png`}
          alt=""
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            pointerEvents: 'none',
          }}
        />

        {/* Word card (top center) — zooms in after whistle to celebrate the answer */}
        <div style={{
          position: 'absolute', top: '3%', left: '50%',
          transform: wordZoom
            ? 'translateX(-50%) scale(1.45)'
            : 'translateX(-50%) scale(1)',
          transformOrigin: 'top center',
          transition: 'transform 0.55s cubic-bezier(.34, 1.56, .64, 1)',
          width: compact ? '62%' : 'clamp(180px, 28%, 380px)',
          zIndex: wordZoom ? 5 : 'auto',
        }}>
          <div style={{ position: 'relative', width: '100%' }}>
            <img
              src={`${ASSETS}/word_box.png`}
              alt=""
              style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
            />
            {/* Inside box: VN hint while playing; full card (word + meaning +
                definition) once celebrated or answer revealed */}
            <div style={{
              position: 'absolute',
              inset: '14% 16% 22% 16%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: wordZoom ? 'center' : 'space-between',
              overflow: 'hidden',
            }}>
              {payload.image_url ? (
                <img
                  src={payload.image_url}
                  alt={payload.word_en}
                  style={{
                    flex: wordZoom ? '0 0 auto' : 1,
                    maxHeight: wordZoom ? '32%' : undefined,
                    width: wordZoom ? 'auto' : '70%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))',
                    minHeight: 0,
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : null}
              <div style={{
                fontSize: compact ? 17 : (wordZoom ? 'clamp(13px, 1.9vw, 20px)' : 'clamp(14px, 2.2vw, 22px)'),
                fontWeight: 800,
                fontFamily: FONT_DISPLAY,
                color: wordZoom ? HT.greenDark : HT.brownDark,
                letterSpacing: -0.3,
                lineHeight: 1.05,
                textAlign: 'center',
                transition: 'color 0.3s',
              }}>
                {wordZoom ? payload.word_en : (payload.name_vi || payload.word_en)}
              </div>
              {wordZoom && payload.name_vi && (
                <div style={{
                  marginTop: 2,
                  fontSize: compact ? 12 : 'clamp(10px, 1.2vw, 13px)',
                  fontWeight: 800, fontFamily: FONT_BODY,
                  color: HT.brownDark, textAlign: 'center', lineHeight: 1.15,
                }}>
                  {payload.name_vi}
                </div>
              )}
              {wordZoom && payload.definition_vi && (
                <div style={{
                  marginTop: 3,
                  fontSize: compact ? 10 : 'clamp(8px, 0.95vw, 11px)',
                  fontWeight: 600, fontFamily: FONT_BODY,
                  color: HT.textSoft, textAlign: 'center', lineHeight: 1.25,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {payload.definition_vi}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sound button — pinned to top-left of the stage */}
        <button
          onClick={replay}
          style={{
            position: 'absolute',
            top: '3%', left: '3%',
            width: 'clamp(36px, 4.2vw, 52px)',
            aspectRatio: '1 / 1',
            borderRadius: '50%', border: `2px solid ${HT.white}`,
            background: HT.coralDark,
            color: HT.white,
            cursor: 'pointer',
            boxShadow: `0 3px 0 ${HT.coralPress}`,
            fontSize: 'clamp(14px, 1.6vw, 20px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0,
            zIndex: 6,
          }}
          title="Nghe lại"
        >🔊</button>

        {/* Train — cars on left, engine on right (DOM order: cars then engine).
            Engine sprite mirrored to face RIGHT. Centered, sits on rail (~34% from
            bottom). Chugs LEFT → RIGHT over 2.6s. */}
        <div style={{
          position: 'absolute',
          bottom: '34%', left: '50%',
          transform: 'translateX(-50%)',
          width: compact ? '94%' : 'auto',
          justifyContent: 'center',
          display: 'flex', alignItems: 'flex-end', gap: '0.4%',
          animation: chugging ? 'tsRoll 2.6s ease-in forwards' : 'none',
          zIndex: 3,
        }}>
          {/* Cars (leading the assembly visually, since engine is now on the right) */}
          {cars.map((tile, i) => {
            const isWrong = grading === 'wrong' && tile;
            return (
              <div
                key={i}
                ref={el => carRefs.current[i] = el}
                onClick={() => tile && ejectFromCar(i)}
                style={{
                  position: 'relative',
                  width: compact ? `${Math.max(13, Math.min(19, 76 / Math.max(1, numCars)))}%` : 'clamp(78px, 12vw, 150px)',
                  flexShrink: 0,
                  cursor: tile ? 'pointer' : 'default',
                  animation: isWrong ? 'tsCarShake 0.35s ease-in-out 2' : 'none',
                  transition: 'transform 0.2s',
                }}
              >
                <img
                  src={`${ASSETS}/train_body_no_question.png`}
                  alt=""
                  style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
                />
                {/* Letter or ? overlay (above wheels area) */}
                <div style={{
                  position: 'absolute',
                  top: '20%', left: 0, right: 0,
                  height: '46%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 800,
                  pointerEvents: 'none',
                }}>
                  {tile ? (
                    <span style={{
                      fontSize: compact ? 22 : 'clamp(26px, 4vw, 52px)',
                      color: HT.text,
                      textShadow: '0 2px 0 rgba(255,255,255,0.5)',
                    }}>{tile.letter}</span>
                  ) : (
                    <span style={{
                      fontSize: compact ? 22 : 'clamp(26px, 4vw, 52px)',
                      color: HT.white,
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    }}>?</span>
                  )}
                </div>
              </div>
            );
          })}

          {/* Engine — last in DOM so it sits on the right of the assembly,
              sprite mirrored (scaleX -1) so it faces RIGHT into direction of travel. */}
          <div style={{
            position: 'relative',
            width: compact ? '20%' : 'clamp(100px, 15vw, 180px)',
            flexShrink: 0,
            animation: chugging ? 'none' : 'tsBob 1.5s ease-in-out infinite',
          }}>
            {chugging && (
              <>
                <div style={{
                  position: 'absolute', top: '-30%', right: '20%', fontSize: '1.8vw',
                  animation: 'tsSmoke 0.8s ease-out infinite',
                }}>💨</div>
                <div style={{
                  position: 'absolute', top: '-40%', right: '30%', fontSize: '1.4vw',
                  animation: 'tsSmoke 0.8s ease-out 0.3s infinite',
                }}>💨</div>
              </>
            )}
            <img
              src={`${ASSETS}/train.png`}
              alt=""
              style={{
                width: '100%', display: 'block', pointerEvents: 'none',
                transform: 'scaleX(-1)',
              }}
            />
          </div>
        </div>

        {/* Letter tray — sized to match the cloze tray. Kept short enough that its
            top edge stays below the train sitting on the track at bottom:34%. */}
        <div style={{
          position: 'absolute',
          bottom: '3%', left: '50%',
          transform: 'translateX(-50%)',
          width: compact ? '96%' : 'clamp(420px, 56%, 780px)',
          aspectRatio: '2172 / 724',
        }}>
          <img
            src={`${ASSETS}/letter_box.png`}
            alt=""
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'fill',
              pointerEvents: 'none',
            }}
          />
          {/* Tile slots inside tray — slots are stable: each letter keeps its
              original position. Placed/flying tiles are hidden in place so the
              remaining tiles don't reflow. */}
          <div style={{
            position: 'absolute',
            inset: '18% 8% 18% 8%',
            display: 'flex', gap: '4%',
            alignItems: 'center', justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            {pool.map(t => (
              <Tile
                key={t.id}
                tileRef={el => tileRefs.current[t.id] = el}
                letter={t.letter}
                isDragging={drag?.id === t.id}
                isHidden={t.placedAt !== null || t.flying}
                dragPoint={drag?.id === t.id ? drag : null}
                onPointerStart={(e) => startDrag(e, t.id)}
              />
            ))}
            {allPlaced && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: HT.yellowSoft, fontSize: 'clamp(10px, 1.2vw, 13px)',
                fontWeight: 700, fontFamily: FONT_BODY,
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                pointerEvents: 'none',
              }}>
                {grading === 'correct' ? '🚂 Tàu khởi hành!' : grading === 'revealed' ? '👀 Đáp án đây nhé' : 'Khay trống'}
              </div>
            )}
          </div>
        </div>

        {/* Confetti when correct */}
        {grading === 'correct' && <Celebration />}

        {/* Wrong banner */}
        {grading === 'wrong' && (
          <div style={{
            position: 'absolute',
            top: '38%', left: '50%', transform: 'translateX(-50%)',
            padding: '6px 14px',
            background: HT.pinkBg,
            border: `2px solid ${HT.coral}`,
            borderRadius: 999,
            color: HT.coralDark,
            fontWeight: 800,
            fontSize: 'clamp(11px, 1.3vw, 14px)',
            fontFamily: FONT_DISPLAY,
            zIndex: 4,
            display: 'flex', gap: 10, alignItems: 'center',
            boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
            whiteSpace: 'nowrap',
          }}>
            😅 Chưa đúng! Bấm vào toa để gỡ chữ
            <button onClick={ejectAll} style={miniBtn(HT.coral)}>↻ Làm lại</button>
            <button onClick={revealAnswer} style={miniBtn(HT.teal)}>👀 Xem đáp án</button>
          </div>
        )}
        {/* Bottom status — floats over stage bottom */}
        {grading === 'correct' && (
          <div style={statusOverlay(HT.greenBg, HT.greenDark)}>
            🎉 Tu... tu! Đoàn tàu lăn bánh với từ <b style={{ color: HT.greenDark }}>{payload.word_en}</b>
          </div>
        )}
        {grading === 'revealed' && (
          <div style={statusOverlay(HT.pinkBg, HT.coral)}>
            👀 Đáp án là <b style={{ color: HT.coralDark }}>{payload.word_en}</b> — lần này chưa tính điểm, lần sau bé tự xếp nhé!
          </div>
        )}
      </div>

      {/* Flying tile overlays — one per in-flight tile so rapid clicks each animate. */}
      {flying.map(f => (
        <div
          key={f.id}
          style={{
            position: 'fixed',
            left: f.startX, top: f.startY,
            width: 'clamp(38px, 5vw, 64px)',
            height: 'clamp(44px, 5.6vw, 72px)',
            background: 'linear-gradient(180deg, #f8c478 0%, #e0a04a 70%, #c08438 100%)',
            border: '2px solid #8b5a2b',
            borderRadius: 5,
            boxShadow: '0 4px 10px rgba(0,0,0,0.3), inset 0 -2px 0 rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 'clamp(20px, 2.8vw, 38px)',
            fontWeight: 800, fontFamily: FONT_DISPLAY,
            color: '#2D2D2D',
            pointerEvents: 'none',
            zIndex: 9999,
            animation: 'tsFly 0.45s cubic-bezier(.5,.05,.4,1.1) forwards',
            '--toX': `${f.toX}px`,
            '--toY': `${f.toY}px`,
          }}
        >
          {f.letter}
        </div>
      ))}
    </div>
  );
}

function statusOverlay(bg, borderColor) {
  return {
    position: 'absolute',
    left: '50%', bottom: '3%',
    transform: 'translateX(-50%)',
    maxWidth: '88%',
    padding: '10px 18px',
    background: bg,
    border: `2px solid ${borderColor}`,
    borderRadius: 14,
    textAlign: 'center',
    fontSize: 'clamp(12px, 1.4vw, 16px)',
    fontWeight: 700,
    color: HT.text,
    fontFamily: FONT_BODY,
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    zIndex: 6,
    whiteSpace: 'nowrap',
  };
}

function Tile({ letter, isDragging, isHidden, dragPoint, onPointerStart, tileRef }) {
  // Wooden Scrabble-style tile
  const style = {
    width: 'clamp(38px, 5vw, 64px)',
    height: 'clamp(44px, 5.6vw, 72px)',
    background: 'linear-gradient(180deg, #f8c478 0%, #e0a04a 70%, #c08438 100%)',
    border: '2px solid #8b5a2b',
    borderRadius: 5,
    boxShadow: '0 2px 0 #6b4720, inset 0 -2px 0 rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 'clamp(20px, 2.8vw, 38px)',
    fontWeight: 800, fontFamily: FONT_DISPLAY,
    color: '#2D2D2D',
    cursor: 'grab', userSelect: 'none', touchAction: 'none',
  };
  // The slot always renders in the flex flow so layout never shifts — only its
  // visibility changes. tileRef stays on the slot so flight start position is
  // always the tile's natural position in the tray.
  const slotHidden = isHidden || isDragging;
  const slot = (
    <div
      ref={tileRef}
      onMouseDown={slotHidden ? undefined : onPointerStart}
      onTouchStart={slotHidden ? undefined : onPointerStart}
      style={{ ...style, visibility: slotHidden ? 'hidden' : 'visible' }}
    >
      {letter}
    </div>
  );
  if (isDragging && dragPoint) {
    return (
      <>
        {slot}
        <div
          className="ts-tile-dragging"
          style={{
            ...style,
            position: 'fixed',
            left: dragPoint.x - 30,
            top: dragPoint.y - 34,
          }}
        >
          {letter}
        </div>
      </>
    );
  }
  return slot;
}

function Celebration() {
  const COLORS = [HT.yellow, HT.coral, HT.green, HT.teal, HT.pink];
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 5 }}>
      {Array.from({ length: 28 }).map((_, i) => {
        const angle = (i / 28) * Math.PI * 2;
        const dist = 200 + Math.random() * 120;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist - 80;
        const dr = (Math.random() - 0.5) * 720;
        return (
          <div key={i} style={{
            position: 'absolute',
            left: '50%', top: '40%',
            width: 10, height: 14,
            background: COLORS[i % COLORS.length],
            borderRadius: i % 2 ? 2 : '50%',
            animation: 'tsRibbon 1.4s ease-out forwards',
            '--dx': `${dx}px`, '--dy': `${dy}px`, '--dr': `${dr}deg`,
          }} />
        );
      })}
    </div>
  );
}

function miniBtn(bg) {
  return {
    padding: '4px 10px', borderRadius: 999, border: 'none',
    background: bg, color: HT.white,
    fontSize: 12, fontWeight: 800, cursor: 'pointer',
    fontFamily: FONT_DISPLAY,
  };
}

